import { useRef, useState, useContext, useLayoutEffect } from "react";
import { CommandBarButton, IconButton, Dialog, DialogType, Stack } from "@fluentui/react";
import { SquareRegular, ErrorCircleRegular } from "@fluentui/react-icons";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import DOMPurify from "dompurify";

import styles from "./Chat.module.css";
import ConfigBox from "./ConfigBox";
import { XSSAllowTags } from "../../constants/xssAllowTags";

import {
    ChatMessage,
    Citation,
    ToolMessageContent,
} from "../../api";
import { Answer } from "../../components/Answer";
import { FeedbackButtons } from "../../components/FeedbackButtons";
import { ToolResponseRenderer, shouldHideTextForCards } from "../../components/ToolResponseRenderer";
import { QuestionInput, QuestionInputMethods } from "../../components/QuestionInput";
import { AppStateContext } from "../../state/AppProvider";
import { useChat } from "../../hooks/useChat";
import type { ToolResultData } from "../../components/ToolResponseRenderer";

/** Renders assistant response: cards (if available) or text. */
const AssistantMessage = ({
    answer,
    prevMessage,
    onCardSelect,
    onCitationClicked,
    parseToolResults,
    parseCitations,
    feedbackEnabled,
}: {
    answer: ChatMessage;
    prevMessage: ChatMessage | undefined;
    onCardSelect: (text: string) => void;
    onCitationClicked: (c: Citation) => void;
    parseToolResults: (msg: ChatMessage) => ToolResultData[];
    parseCitations: (msg: ChatMessage) => Citation[];
    feedbackEnabled: boolean;
}) => {
    const toolResults = prevMessage ? parseToolResults(prevMessage) : [];
    const hideText = shouldHideTextForCards(toolResults);
    return (
        <div className={styles.chatMessageGpt}>
            <ToolResponseRenderer toolResults={toolResults} onCardSelect={onCardSelect} />
            {!hideText && (
                <Answer
                    answer={{
                        answer: answer.content,
                        citations: prevMessage ? parseCitations(prevMessage) : [],
                        message_id: answer.id,
                        feedback: answer.feedback,
                    }}
                    onCitationClicked={onCitationClicked}
                />
            )}
            {/* Operator OK/NG label — only once the response has a trace_id
                (set on stream `done`) and the backend has feedback enabled. */}
            {feedbackEnabled && !hideText && answer.trace_id && (
                <FeedbackButtons traceId={answer.trace_id} />
            )}
        </div>
    );
};

const Chat = () => {
    const appStateContext = useContext(AppStateContext);
    const ui = appStateContext?.state.frontendSettings?.ui;
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const questionInputRef = useRef<QuestionInputMethods | null>(null);
    const [activeCitation, setActiveCitation] = useState<Citation>();
    const [isCitationPanelOpen, setIsCitationPanelOpen] = useState(false);
    const [clearingChat, setClearingChat] = useState(false);

    const {
        messages,
        isLoading,
        showLoadingMessage,
        statusStage,
        processMessages,
        errorMsg,
        hideErrorDialog,
        makeApiRequest,
        clearChat: clearChatHook,
        newChat,
        stopGenerating,
        handleErrorDialogClose,
    } = useChat();

    const ASSISTANT = "assistant";
    const ERROR = "error";

    // Progress text for the loading indicator, driven by the SSE status stage.
    const statusLabel =
        statusStage === "searching"
            ? "Searching NURA's knowledge..."
            : statusStage === "generating"
              ? "Generating answer..."
              : "Thinking...";

    // ── auto-scroll ─────────────────────────────────────────

    useLayoutEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    }, [showLoadingMessage, processMessages]);

    // ── citation helpers ────────────────────────────────────

    const onShowCitation = (citation: Citation) => {
        setActiveCitation(citation);
        setIsCitationPanelOpen(true);
    };

    const onViewSource = (citation: Citation) => {
        if (citation.url && !citation.url.includes("blob.core")) {
            window.open(citation.url, "_blank");
        }
    };

    const parseToolMessage = (message: ChatMessage) => {
        if (message?.role === "tool") {
            try {
                return JSON.parse(message.content) as ToolMessageContent;
            } catch {
                return null;
            }
        }
        return null;
    };

    const parseCitationFromMessage = (message: ChatMessage) => {
        const parsed = parseToolMessage(message);
        return parsed?.citations ?? [];
    };

    const parseToolResultsFromMessage = (message: ChatMessage) => {
        const parsed = parseToolMessage(message);
        return parsed?.tool_results ?? [];
    };

    const onCardSelect = (text: string) => {
        if (!isLoading) {
            makeApiRequest(text, appStateContext?.state.currentChat?.id ?? undefined);
        }
    };

    // ── clear chat wrapper ──────────────────────────────────

    const clearChat = async () => {
        setClearingChat(true);
        await clearChatHook();
        setActiveCitation(undefined);
        setIsCitationPanelOpen(false);
        setClearingChat(false);
    };

    // ── helpers ─────────────────────────────────────────────

    const disabledButton = () =>
        isLoading ||
        (messages && messages.length === 0) ||
        clearingChat;

    const onClickExample = (question: string) => {
        questionInputRef.current?.setQuesionExample(question);
    };

    // When the latest message is a card (e.g. booking slots), the answer text is
    // hidden — so suppress the "Generating answer..." loading bubble, otherwise it
    // dangles under the card until generation of the (hidden) text finishes.
    const lastMessage = messages?.[messages.length - 1];
    const lastMessageHasCard =
        lastMessage?.role === "tool" &&
        shouldHideTextForCards(parseToolResultsFromMessage(lastMessage));

    // ── render ──────────────────────────────────────────────

    return (
        <div className={styles.container} role="main">
            <Stack horizontal className={styles.chatRoot}>
                <div className={styles.chatContainer}>
                    {!messages || messages.length < 1 ? (
                        <Stack className={styles.chatEmptyState}>
                            <h1 className={styles.chatEmptyStateTitle}>{ui?.chat_title}</h1>
                            <h2 className={styles.chatEmptyStateSubtitle}>{ui?.chat_description}</h2>
                            <ConfigBox onClickExample={onClickExample} />
                        </Stack>
                    ) : (
                        <div
                            className={styles.chatMessageStream}
                            role="log"
                        >
                            {messages.map((answer, index) => (
                                <div key={answer.id || index}>
                                    {answer.role === "user" ? (
                                        <div className={styles.chatMessageUser} tabIndex={0}>
                                            <div className={styles.chatMessageUserMessage}>
                                                {answer.content}
                                            </div>
                                        </div>
                                    ) : answer.role === ASSISTANT ? (
                                        <AssistantMessage
                                            answer={answer}
                                            prevMessage={messages[index - 1]}
                                            onCardSelect={onCardSelect}
                                            onCitationClicked={onShowCitation}
                                            parseToolResults={parseToolResultsFromMessage}
                                            parseCitations={parseCitationFromMessage}
                                            feedbackEnabled={!!appStateContext?.state.frontendSettings?.feedback_enabled}
                                        />
                                    ) : answer.role === "tool" && index === messages.length - 1 ? (
                                        // During streaming the tool message (cards) can arrive
                                        // before the assistant message. Render its cards standalone
                                        // while it is the last message so the card shows immediately;
                                        // once the assistant message follows, AssistantMessage renders
                                        // the cards from prevMessage instead (no duplication).
                                        <div className={styles.chatMessageGpt}>
                                            <ToolResponseRenderer
                                                toolResults={parseToolResultsFromMessage(answer)}
                                                onCardSelect={onCardSelect}
                                            />
                                        </div>
                                    ) : answer.role === ERROR ? (
                                        <div className={styles.chatMessageError}>
                                            <Stack
                                                horizontal
                                                className={styles.chatMessageErrorContent}
                                            >
                                                <ErrorCircleRegular
                                                    className={styles.errorIcon}
                                                    style={{ color: "rgba(182, 52, 67, 1)" }}
                                                />
                                                <span>Error</span>
                                            </Stack>
                                            <span className={styles.chatMessageErrorContent}>
                                                {answer.content}
                                            </span>
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                            {showLoadingMessage && !lastMessageHasCard && (
                                <div className={styles.chatMessageGpt}>
                                    <Answer
                                        answer={{ answer: statusLabel, citations: [] }}
                                        onCitationClicked={() => null}
                                    />
                                </div>
                            )}
                            <div ref={chatMessageStreamEnd} />
                        </div>
                    )}

                    {isLoading && (
                        <Stack
                            horizontal
                            className={styles.stopGeneratingContainer}
                            role="button"
                            aria-label="Stop generating"
                            tabIndex={0}
                            onClick={stopGenerating}
                            onKeyDown={(e) =>
                                e.key === "Enter" || e.key === " "
                                    ? stopGenerating()
                                    : null
                            }
                        >
                            <SquareRegular
                                className={styles.stopGeneratingIcon}
                                aria-hidden="true"
                            />
                            <span className={styles.stopGeneratingText} aria-hidden="true">
                                Stop generating
                            </span>
                        </Stack>
                    )}

                    <Stack horizontal className={styles.chatInput}>
                        <CommandBarButton
                            role="button"
                            styles={{
                                icon: { color: "#FFFFFF" },
                                iconDisabled: { color: "#BDBDBD !important" },
                                root: {
                                    color: "#FFFFFF",
                                    background:
                                        "radial-gradient(109.81% 107.82% at 100.1% 90.19%,#e8e80a 33.63%,#8DDDD8 100%)",
                                },
                                rootDisabled: { background: "#F0F0F0" },
                            }}
                            className={styles.clearChatBroomNoCosmos}
                            iconProps={{ iconName: "Broom" }}
                            onClick={newChat}
                            disabled={disabledButton()}
                            aria-label="clear chat button"
                        />
                        <Dialog
                            hidden={hideErrorDialog}
                            onDismiss={handleErrorDialogClose}
                            dialogContentProps={{
                                type: DialogType.close,
                                title: errorMsg?.title,
                                closeButtonAriaLabel: "Close",
                                subText: errorMsg?.subtitle,
                            }}
                            modalProps={{
                                titleAriaId: "labelId",
                                subtitleAriaId: "subTextId",
                                isBlocking: true,
                                styles: { main: { maxWidth: 450 } },
                            }}
                        />
                        <QuestionInput
                            clearOnSend
                            placeholder="Type a new question..."
                            disabled={isLoading}
                            onSend={(question, id) => makeApiRequest(question, id)}
                            conversationId={appStateContext?.state.currentChat?.id ?? undefined}
                            ref={questionInputRef}
                        />
                    </Stack>
                </div>

                {/* Citation Panel */}
                {messages &&
                    messages.length > 0 &&
                    isCitationPanelOpen &&
                    activeCitation && (
                        <Stack.Item
                            className={styles.citationPanel}
                            tabIndex={0}
                            role="tabpanel"
                            aria-label="Citations Panel"
                        >
                            <Stack
                                aria-label="Citations Panel Header Container"
                                horizontal
                                className={styles.citationPanelHeaderContainer}
                                horizontalAlign="space-between"
                                verticalAlign="center"
                            >
                                <span
                                    aria-label="Citations"
                                    className={styles.citationPanelHeader}
                                >
                                    Citations
                                </span>
                                <IconButton
                                    iconProps={{ iconName: "Cancel" }}
                                    aria-label="Close citations panel"
                                    onClick={() => setIsCitationPanelOpen(false)}
                                />
                            </Stack>
                            <h5
                                className={styles.citationPanelTitle}
                                tabIndex={0}
                                title={
                                    activeCitation.url &&
                                    !activeCitation.url.includes("blob.core")
                                        ? activeCitation.url
                                        : activeCitation.title ?? ""
                                }
                                onClick={() => onViewSource(activeCitation)}
                            >
                                {activeCitation.title}
                            </h5>
                            <div tabIndex={0}>
                                <ReactMarkdown
                                    linkTarget="_blank"
                                    className={styles.citationPanelContent}
                                    children={DOMPurify.sanitize(activeCitation.content, {
                                        ALLOWED_TAGS: XSSAllowTags,
                                    })}
                                    remarkPlugins={[remarkGfm]}
                                    rehypePlugins={[rehypeRaw]}
                                />
                            </div>
                        </Stack.Item>
                    )}

            </Stack>
        </div>
    );
};

export default Chat;
